/**
 * ehr#743 (V11): "Search everything" on the client chart's Profile tab. One bar, pinned under the
 * top tabs (it sits outside the scrolling body), with a results list under it: the open section's
 * matches first, up to 10, then "N more matches, keep typing to narrow". The list shortens to stay
 * above the footer and never scrolls inside itself. "/" jumps to the bar; arrows move through the
 * results, Enter or a click opens one (the chart flashes the field), Escape closes the list.
 * Typing only changes the list (Jay's ruling 32A): the fields on the page stay as they are.
 */
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import {
  isSlashShortcut,
  markHead,
  markMatch,
  markValue,
  moreLine,
  rowsThatFit,
  searchChart,
  type ChartHit,
  type Mark,
} from '../lib/chartSearch.js';

const LIST_ID = 'chart-search-results';

function Marked({ parts }: { parts: Mark[] }) {
  return <>{parts.map((p, i) => (p.hit ? <mark key={i}>{p.text}</mark> : p.text))}</>;
}

export function ChartSearch({
  items,
  currentSection,
  onPick,
  initialQuery = '',
}: {
  items: readonly ChartHit[];
  /** The open Profile section: its matches list first. */
  currentSection: string | null;
  /** `root` is the chart (the drawer) the bar sits in, to find the field to flash. */
  onPick: (hit: ChartHit, root: ParentNode) => void;
  /** For tests (server render): start with this typed and the list open. */
  initialQuery?: string;
}) {
  const [q, setQ] = useState(initialQuery);
  const [open, setOpen] = useState(!!initialQuery);
  const [act, setAct] = useState(-1);
  // Rows kept so the list stays above the footer, measured for one query / section / window size
  // (a fit measured for another key is ignored, so nothing has to reset it).
  const [fit, setFit] = useState<{ key: string; k: number } | null>(null);
  const [sizeTick, setSizeTick] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const blurT = useRef<number | undefined>(undefined);

  const res = useMemo(() => searchChart(items, q, currentSection), [items, q, currentSection]);
  const fitKey = [q, currentSection ?? '', sizeTick].join('|');
  const fitK = fit && fit.key === fitKey ? fit.k : null;
  const shown = fitK != null ? res.shown.slice(0, fitK) : res.shown;
  const more = res.total - shown.length;
  const showList = open && q.trim() !== '';

  useEffect(() => setAct(-1), [q, currentSection]);

  // Shorten the list until it clears the footer (V10 fix 1). The list never scrolls inside itself.
  useLayoutEffect(() => {
    if (!showList || fitK != null) return;
    const list = listRef.current;
    if (!list) return;
    const host = wrapRef.current?.closest('[role="dialog"]') ?? document.body;
    const foot = host.querySelector('.drawer-foot');
    // The footer's top, or the window's bottom when the footer is lower (phones); a little air.
    const limit =
      Math.min(foot ? foot.getBoundingClientRect().top : Infinity, window.innerHeight) - 8;
    if (list.getBoundingClientRect().bottom <= limit) return;
    const rows = Array.from(list.querySelectorAll<HTMLElement>('.chart-search-row'));
    const moreEl = list.querySelector<HTMLElement>('.chart-search-more');
    const k = rowsThatFit(
      rows.map((r) => r.getBoundingClientRect().bottom),
      limit,
      // the "more matches" line with its top margin, plus the list's own bottom padding
      (moreEl?.getBoundingClientRect().height || 34) + 12,
      !!moreEl,
    );
    if (k < rows.length) setFit({ key: fitKey, k });
  });
  useEffect(() => {
    // Measure again when the window really changes size (a phone fires `resize` as the list
    // itself grows and shrinks; reacting to that would undo the fit).
    let size = `${window.innerWidth}x${window.innerHeight}`;
    const onResize = () => {
      const now = `${window.innerWidth}x${window.innerHeight}`;
      if (now === size) return;
      size = now;
      setSizeTick((t) => t + 1);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // "/" from anywhere while the bar is on screen, which for now is the Profile tab only (not while
  // typing, and not under a pop-up over it). "/" from every tab comes with every-tab search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isSlashShortcut(e)) return;
      const host = wrapRef.current?.closest('[role="dialog"]') ?? null;
      const t = e.target as HTMLElement | null;
      const over = t?.closest?.('[role="dialog"], [role="alertdialog"]') ?? null;
      if (over && over !== host) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => () => window.clearTimeout(blurT.current), []);

  const pick = (hit: ChartHit) => {
    setOpen(false);
    setAct(-1);
    const root = wrapRef.current?.closest('[role="dialog"]') ?? document;
    onPick(hit, root);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (showList) setOpen(false);
      else inputRef.current?.blur();
      return;
    }
    if (!showList || !shown.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAct((a) => (a + 1) % shown.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAct((a) => (a - 1 + shown.length) % shown.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = shown[Math.max(act, 0)];
      if (hit) pick(hit);
    }
  };

  const optId = (h: ChartHit) => `chart-search-opt-${h.id.replace(/[^\w-]/g, '_')}`;
  const activeHit = act > -1 ? shown[act] : undefined;

  return (
    <div className="chart-search-bar">
      <div className="chart-search" ref={wrapRef}>
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={inputRef}
          type="search"
          className="chart-search-input"
          role="combobox"
          aria-label="Search everything"
          aria-controls={LIST_ID}
          aria-expanded={showList}
          aria-autocomplete="list"
          aria-activedescendant={showList && activeHit ? optId(activeHit) : undefined}
          autoComplete="off"
          placeholder="Search everything: name, DOB, insurance, pharmacy, referral, balance..."
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            window.clearTimeout(blurT.current); // V10 fix 4: clicking straight back in keeps it
            setOpen(true);
          }}
          onBlur={() => {
            window.clearTimeout(blurT.current);
            blurT.current = window.setTimeout(() => {
              if (document.activeElement !== inputRef.current) setOpen(false);
            }, 120);
          }}
          onKeyDown={onKeyDown}
        />
        {showList && (
          <div className="chart-search-results" id={LIST_ID} role="listbox" ref={listRef}>
            {shown.length ? (
              shown.map((h, i) => (
                <div
                  key={h.id}
                  id={optId(h)}
                  role="option"
                  aria-selected={i === act}
                  className={`chart-search-row${i === act ? ' act' : ''}`}
                  // mousedown, so the bar keeps focus until the field takes it
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(h);
                  }}
                  onMouseEnter={() => setAct(i)}
                >
                  <span className="chart-search-main">
                    <Marked parts={h.kind === 'head' ? markHead(h, q) : markMatch(h.label, q)} />
                    {h.kind !== 'head' && h.value && (
                      <span className="chart-search-val">
                        <Marked parts={markValue(h, q)} />
                      </span>
                    )}
                  </span>
                  <span className="chart-search-where">{h.where}</span>
                </div>
              ))
            ) : (
              <div className="chart-search-empty">Nothing matches “{q.trim()}”</div>
            )}
            {more > 0 && <div className="chart-search-more">{moreLine(more)}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
